import { useQuery } from "@tanstack/react-query";
import { fromTable } from "@/integrations/supabase/db";

export interface ImageSelectOption {
  value: string;
  label: string;
  icon?: string;
  image?: string;
}

export interface TableRow {
  id: string;
  label: string;
  type?: 'text' | 'number' | 'currency';
}

export interface CalculatorRow {
  id: string;
  label: string;
  type: 'input' | 'calculated' | 'sum';
  formula?: string;
  category?: 'einnahmen' | 'ausgaben';
}

export interface BeratungFormField {
  id: string;
  type: 
    | 'text' | 'email' | 'tel' | 'number' | 'date' | 'select' | 'checkbox' | 'currency'
    | 'image-select' | 'timeline-slider' | 'star-rating' | 'dual-column-table' 
    | 'calculator' | 'radio-group' | 'notes' | 'textarea'
    | 'multi-person-table' | 'children-data-table' | 'income-expense-table' | 'insurance-checklist-table';
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  imageOptions?: ImageSelectOption[];
  tableRows?: TableRow[];
  calculatorRows?: CalculatorRow[];
  column1Label?: string;
  column2Label?: string;
  multiple?: boolean;
  columns?: 2 | 3 | 4;
  maxStars?: number;
  starLabels?: string[];
  orientation?: 'horizontal' | 'vertical';
  default?: any;
  conditional?: {
    field: string;
    value: any;
  };
}

export interface BeratungRankingItem {
  id: string;
  label: string;
  icon?: string;
}

export interface BeraterLayout {
  type: 'form' | 'ranking' | 'goals' | 'checklist' | 'gap-analysis';
  title?: string;
  fields?: BeratungFormField[];
  items?: BeratungRankingItem[];
  timeframe?: string;
  categories?: string[];
}

export interface PriorityTile {
  id: string;
  label: string;
  icon?: string;
  image?: string;
  /** Column span in 12-column grid (1-12) */
  colSpan?: number;
  /** Row span in grid (1-12) */
  rowSpan?: number;
  /** Image position X (0-100%) */
  imagePositionX?: number;
  /** Image position Y (0-100%) */
  imagePositionY?: number;
  /** Image scale/zoom (100 = 100%) */
  imageScale?: number;
}

export interface ServiceItem {
  id: string;
  label: string;
  icon: string;
  active?: boolean;
}

export interface PyramidLayer {
  label: string;
  value?: number;
}

export interface BenefitItem {
  id: string;
  text: string;
  highlighted?: boolean;
}

export interface LifecyclePhase {
  ageRange: string;
  label: string;
  products: string[];
  icon?: string;
}

export interface KundenLayout {
  type: 
    | 'summary' | 'map' | 'chart' | 'ranking-visual' | 'timeline' | 'future-vision' 
    | 'insurance-wheel' | 'gap-visual' | 'priority-tiles' | 'service-map'
    | 'rent-timeline' | 'financing-pyramid' | 'benefit-checklist' | 'pension-gap'
    | 'risk-profile' | 'lifecycle-timeline' | 'income-value' | 'signature-panel' | 'feedback'
    | 'savings-comparison' | 'generational-ratio' | 'investment-profile' | 'workforce-value'
    | 'child-provision' | 'retirement-planning'
    | 'investment-view' | 'household-budget' | 'property-insurance' | 'income-protection' | 'referral-feedback';
  title: string;
  icon?: string;
  description?: string;
  subtitle?: string;
  animated?: boolean;
  showAddress?: boolean;
  showGaps?: boolean;
  chartType?: string;
  timeframe?: string;
  // Priority Tiles
  tiles?: PriorityTile[];
  // Service Map
  services?: ServiceItem[];
  // Rent Timeline
  startMiete?: number;
  steigerung?: number;
  jahre?: number;
  illustrationUrl?: string;
  // Financing Pyramid
  layers?: PyramidLayer[];
  // Benefit Checklist
  benefits?: BenefitItem[];
  columns?: 1 | 2;
  leftImageUrl?: string;
  rightImageUrl?: string;
  // Lifecycle
  phases?: LifecyclePhase[];
  // Income Value
  retirementAge?: number;
  // Child Provision
  monatlicheEinzahlung?: number;
  laufzeitJahre?: number;
}

export interface BeratungSeite {
  id: string;
  modul_id: string | null;
  beratung_sparte_id: string | null;
  titel: string;
  untertitel: string | null;
  reihenfolge: number;
  nur_kunde: boolean;
  pflichtseite: boolean;
  berater_layout: BeraterLayout;
  kunden_layout: KundenLayout;
  priority_mapping: string[] | null;
  shared_content_id: string | null;
  created_at: string;
  updated_at: string;
}

interface UseBeratungSeitenOptions {
  modulIds?: string[];
  enabled?: boolean;
}

export function useBeratungSeiten({ modulIds, enabled = true }: UseBeratungSeitenOptions = {}) {
  return useQuery({
    queryKey: ['beratung-seiten', modulIds],
    queryFn: async (): Promise<BeratungSeite[]> => {
      if (!modulIds || modulIds.length === 0) return [];
      
      const { data, error } = await fromTable('beratung_seiten')
        .select('*')
        .in('modul_id', modulIds)
        .order('modul_id', { ascending: true })
        .order('reihenfolge', { ascending: true });

      if (error) throw error;
      
      return (data || []) as BeratungSeite[];
    },
    enabled: enabled && !!modulIds && modulIds.length > 0,
  });
}
